import { PERMISSION_CODES } from "@azm/shared/enums/permissions";
import type { AuthService } from "@azm/core/services/auth";
import type { ISidebarItem } from "@azm/core/interfaces/sidebar-item";
import { resolveSidebarRoute } from "@azm/core/constants/sidebar-routes";

const LAWSUITS_MENU = [
	PERMISSION_CODES.LAWSUITS_CAN_VIEW,
	PERMISSION_CODES.LAWSUITS_CAN_CREATE,
	PERMISSION_CODES.COMPLAINTS_CAN_VIEW_ALL,
	PERMISSION_CODES.LAWSUITS_CAN_VIEW_SAVED,
	PERMISSION_CODES.LAWSUITS_CAN_VIEW_ASSIGNED,
	PERMISSION_CODES.LAWSUITS_CAN_VIEW_DETAILS,
] as const;

const NOTES_MENU = [
	PERMISSION_CODES.NOTES_CAN_VIEW,
	PERMISSION_CODES.NOTES_CAN_CREATE,
	PERMISSION_CODES.NOTICES_CAN_VIEW_ALL,
	PERMISSION_CODES.NOTES_CAN_VIEW_SAVED,
	PERMISSION_CODES.NOTES_CAN_VIEW_ASSIGNED,
	PERMISSION_CODES.NOTES_CAN_VIEW_DETAILS,
] as const;

const PM_MENU = [
	PERMISSION_CODES.PM_CAN_VIEW,
	PERMISSION_CODES.PM_CAN_CREATE,
	PERMISSION_CODES.MONITORING_CAN_VIEW_ALL,
	PERMISSION_CODES.PM_CAN_VIEW_SAVED,
	PERMISSION_CODES.PM_CAN_VIEW_ASSIGNED,
	PERMISSION_CODES.PM_CAN_VIEW_DETAILS,
] as const;

const USERS_MENU = [
	PERMISSION_CODES.USERS_CAN_VIEW,
	PERMISSION_CODES.USERS_CAN_VIEW_DETAILS,
	PERMISSION_CODES.USERS_CAN_CREATE,
	PERMISSION_CODES.USERS_CAN_EDIT,
	PERMISSION_CODES.USERS_CAN_RESET_PASSWORD,
] as const;

const REPORTS_MENU = [
	PERMISSION_CODES.REPORTS_CAN_VIEW,
	PERMISSION_CODES.REPORTS_CAN_VIEW_LAWSUITS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_NOTICES,
	PERMISSION_CODES.REPORTS_CAN_VIEW_PROFESSIONAL_MONITORING,
	PERMISSION_CODES.REPORTS_CAN_VIEW_INTERNAL_USERS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_BENEFICIARIES,
	PERMISSION_CODES.REPORTS_CAN_VIEW_LAWYERS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_BUSINESS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_GOVERNMENT,
	PERMISSION_CODES.REPORTS_CAN_VIEW_STATISTICS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_REQUESTS_STATUS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_SMS_LOGS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_SAVED_REQUESTS,
	PERMISSION_CODES.REPORTS_CAN_VIEW_COMPREHENSIVE,
	PERMISSION_CODES.REPORTS_CAN_VIEW_REFERRED_TO_DEPARTMENT,
	PERMISSION_CODES.REPORTS_CAN_VIEW_UNDER_REVIEW,
	PERMISSION_CODES.REPORTS_CAN_VIEW_REFERRED_TO_POLICE,
	PERMISSION_CODES.REPORTS_CAN_VIEW_AWAITING_STATEMENT,
	PERMISSION_CODES.REPORTS_CAN_VIEW_AWAITING_REQUIREMENTS,
] as const;

const SIDEBAR_ITEM_PERMISSIONS: Record<string, readonly string[]> = {
	dashboard: [PERMISSION_CODES.DASHBOARD_CAN_VIEW],
	lawsuits: LAWSUITS_MENU,
	requests: LAWSUITS_MENU,
	complaints: LAWSUITS_MENU,
	complaint: LAWSUITS_MENU,
	notices: NOTES_MENU,
	notes: NOTES_MENU,
	"professional-monitoring": PM_MENU,
	notifications: [PERMISSION_CODES.PROFILE_CAN_VIEW],
	profile: [PERMISSION_CODES.PROFILE_CAN_VIEW, PERMISSION_CODES.PROFILE_CAN_EDIT, PERMISSION_CODES.PROFILE_CAN_CHANGE_PASSWORD],
	users: USERS_MENU,
	roles: [PERMISSION_CODES.USERS_CAN_VIEW_ROLES],
	"users/roles": [PERMISSION_CODES.USERS_CAN_VIEW_ROLES],
	"account-requests": [
		PERMISSION_CODES.ACCOUNT_REQUESTS_CAN_VIEW,
		PERMISSION_CODES.ACCOUNT_REQUESTS_CAN_VIEW_DETAILS,
		PERMISSION_CODES.ACCOUNT_REQUESTS_CAN_RESPOND,
	],
	referral: [PERMISSION_CODES.REFERRAL_CAN_VIEW, PERMISSION_CODES.REFERRAL_CAN_CREATE, PERMISSION_CODES.LAWSUITS_CAN_LOOKUP_BY_REF],
	reports: REPORTS_MENU,
	"reports-complaints": [PERMISSION_CODES.REPORTS_CAN_VIEW_LAWSUITS],
	"reports-notices": [PERMISSION_CODES.REPORTS_CAN_VIEW_NOTICES],
	"reports-professional-monitoring": [PERMISSION_CODES.REPORTS_CAN_VIEW_PROFESSIONAL_MONITORING],
	"reports-comprehensive": [PERMISSION_CODES.REPORTS_CAN_VIEW_COMPREHENSIVE],
	"reports-users": [
		PERMISSION_CODES.REPORTS_CAN_VIEW_USERS,
		PERMISSION_CODES.REPORTS_CAN_VIEW_INTERNAL_USERS,
		PERMISSION_CODES.REPORTS_CAN_VIEW_LAWYERS,
		PERMISSION_CODES.REPORTS_CAN_VIEW_BUSINESS,
		PERMISSION_CODES.REPORTS_CAN_VIEW_GOVERNMENT,
	],
	"reports-beneficiaries": [PERMISSION_CODES.REPORTS_CAN_VIEW_BENEFICIARIES],
	"reports-statistics": [PERMISSION_CODES.REPORTS_CAN_VIEW_STATISTICS],
	"reports-requests-status": [PERMISSION_CODES.REPORTS_CAN_VIEW_REQUESTS_STATUS],
	"reports-sms-logs": [PERMISSION_CODES.REPORTS_CAN_VIEW_SMS_LOGS],
	"reports-saved-requests": [PERMISSION_CODES.REPORTS_CAN_VIEW_SAVED_REQUESTS],
	"reports-referred-to-department": [PERMISSION_CODES.REPORTS_CAN_VIEW_REFERRED_TO_DEPARTMENT],
	"reports-under-review": [PERMISSION_CODES.REPORTS_CAN_VIEW_UNDER_REVIEW],
	"reports-referred-to-police": [PERMISSION_CODES.REPORTS_CAN_VIEW_REFERRED_TO_POLICE],
	"reports-awaiting-statement": [PERMISSION_CODES.REPORTS_CAN_VIEW_AWAITING_STATEMENT],
	"reports-awaiting-requirements": [PERMISSION_CODES.REPORTS_CAN_VIEW_AWAITING_REQUIREMENTS],
	statics: [PERMISSION_CODES.REPORTS_CAN_VIEW_STATISTICS],
	"transaction-lifespan": [PERMISSION_CODES.TRANSACTION_AGE_CAN_VIEW],
	"holidays-management": [PERMISSION_CODES.HOLIDAYS_MANAGEMENT_CAN_VIEW],
	"delegation-management": [PERMISSION_CODES.DELEGATION_MANAGEMENT_CAN_VIEW],
	"delegated-requests": [PERMISSION_CODES.DELEGATED_REQUESTS_CAN_VIEW],
	"glad-referrals": [PERMISSION_CODES.GLAD_REFERRALS_CAN_VIEW],
	"audit-log": [
		PERMISSION_CODES.AUDIT_LOG_CAN_VIEW,
		PERMISSION_CODES.AUDIT_LOG_CAN_VIEW_GENERAL,
		PERMISSION_CODES.AUDIT_LOG_CAN_VIEW_TRANSACTIONS,
	],
	"audit-log-transactions": [PERMISSION_CODES.AUDIT_LOG_CAN_VIEW_TRANSACTIONS],
	"audit-log-general": [PERMISSION_CODES.AUDIT_LOG_CAN_VIEW_GENERAL],
	"audit-log/transactions": [PERMISSION_CODES.AUDIT_LOG_CAN_VIEW_TRANSACTIONS],
	"audit-log/general": [PERMISSION_CODES.AUDIT_LOG_CAN_VIEW_GENERAL],
	feedback: [PERMISSION_CODES.FEEDBACK_CAN_VIEW, PERMISSION_CODES.FEEDBACK_CAN_VIEW_DETAILS, PERMISSION_CODES.FEEDBACK_CAN_RESPOND],
	"conversation-history": [
		PERMISSION_CODES.CONVERSATION_HISTORY_CAN_VIEW,
		PERMISSION_CODES.CONVERSATION_HISTORY_CAN_VIEW_DETAILS,
	],
};

function itemKeys(item: { code?: string; routerLinkName?: string }): string[] {
	return [item.routerLinkName, item.code].filter((key): key is string => !!key).map((key) => key.trim().toLowerCase());
}

export function requiredPermissionsForSidebarItem(item: { code?: string; routerLinkName?: string }): readonly string[] | null {
	const keys = itemKeys(item);
	for (const key of keys) {
		if (Object.prototype.hasOwnProperty.call(SIDEBAR_ITEM_PERMISSIONS, key)) {
			return SIDEBAR_ITEM_PERMISSIONS[key] ?? null;
		}
	}
	if (keys.some((key) => key.includes("notification"))) {
		return [PERMISSION_CODES.PROFILE_CAN_VIEW];
	}
	return null;
}

export function filterSidebarItemsByPermission(items: ISidebarItem[], auth: AuthService): ISidebarItem[] {
	void auth.userPermissions();
	void auth.permissionsLoaded();
	if (!auth.permissionsApplyToCurrentUser()) return items;
	if (!auth.permissionsLoaded()) return items;

	const next: ISidebarItem[] = [];
	for (const item of items) {
		const children =
			"children" in item && item.children?.length ? filterSidebarItemsByPermission(item.children as ISidebarItem[], auth) : undefined;
		const required = requiredPermissionsForSidebarItem(item);
		const allowed = !required || (required.length > 0 && auth.hasAnyPermission([...required]));

		if ("children" in item && item.children) {
			if (!allowed && !children?.length) continue;
			next.push({ ...item, children: children ?? [] } as ISidebarItem);
			continue;
		}

		if (allowed) next.push(item);
	}
	return next;
}

function firstNavigableRoute(item: ISidebarItem): string | undefined {
	if ("children" in item && item.children?.length) {
		for (const child of item.children) {
			const path = firstNavigableRoute(child as ISidebarItem);
			if (path) return path;
		}
		return undefined;
	}
	if ("routerLinkName" in item) {
		return resolveSidebarRoute(item.routerLinkName);
	}
	return undefined;
}

export function firstAllowedSidebarPath(items: ISidebarItem[], auth: AuthService): string | null {
	const visible = filterSidebarItemsByPermission(
		items.filter((item) => !("children" in item && item.code === "help")),
		auth,
	);
	for (const item of visible) {
		const path = firstNavigableRoute(item);
		if (path) return `/${path}`;
	}
	return null;
}
